import * as React from "react";
import { cn } from "@/lib/utils";

export interface PublicCollectionItemSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Loading skeleton for PublicCollectionItem component
 *
 * Matches the PublicCollectionItem layout with animated pulse effect.
 * Used to show loading state while fetching Trophy Room items.
 *
 * @example
 * ```tsx
 * <PublicCollectionItemSkeleton />
 * ```
 */
export const PublicCollectionItemSkeleton = React.forwardRef<
  HTMLDivElement,
  PublicCollectionItemSkeletonProps
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="status"
      aria-label="Loading collection item"
      className={cn(
        "flex flex-col gap-[1rem] items-start w-full animate-pulse",
        className
      )}
      {...props}
    >
      {/* Product Image Skeleton */}
      <div className="relative w-full aspect-square bg-gray-100 rounded-[0.3125rem]" />

      {/* Product Details Skeleton */}
      <div className="flex flex-col gap-[0.5rem] items-start w-full">
        {/* Brand Name Skeleton */}
        <div className="h-[1.25rem] bg-gray-100 rounded w-1/4" />

        {/* Product Name and Edition Number (Same Line) */}
        <div className="flex items-baseline justify-between w-full gap-[1rem]">
          {/* Product Name Skeleton */}
          <div className="h-[1.25rem] bg-gray-100 rounded flex-1" />

          {/* Product Number Skeleton */}
          <div className="h-[1.25rem] bg-gray-100 rounded w-16 shrink-0" />
        </div>
      </div>
    </div>
  );
});

PublicCollectionItemSkeleton.displayName = "PublicCollectionItemSkeleton";
